"use client";

import { createContext, useContext, useState, useCallback, ReactNode, ReactElement } from "react";

export interface HeaderAction {
  id: string;
  element: ReactElement;
}

export interface HeaderLabel {
  text: string;
}

interface HeaderActionsContextType {
  actions: HeaderAction[];
  centerActions: HeaderAction[];
  label?: HeaderLabel;
  headerClasses: string[];
  registerAction: (action: HeaderAction) => void;
  unregisterAction: (id: string) => void;
  registerCenterAction: (action: HeaderAction) => void;
  unregisterCenterAction: (id: string) => void;
  setLabel: (label: HeaderLabel) => void;
  clearLabel: () => void;
  addHeaderClass: (className: string) => void;
  removeHeaderClass: (className: string) => void;
  clearHeaderClasses: () => void;
}

const HeaderActionsContext = createContext<HeaderActionsContextType | undefined>(undefined);

export function HeaderActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<HeaderAction[]>([]);
  const [centerActions, setCenterActions] = useState<HeaderAction[]>([]);
  const [label, setLabelState] = useState<HeaderLabel | undefined>(undefined);
  const [headerClasses, setHeaderClasses] = useState<string[]>([]);

  const registerAction = useCallback((action: HeaderAction) => {
    setActions((prev) => {
      // Remove existing action with same id if it exists
      const filtered = prev.filter((a) => a.id !== action.id);
      return [...filtered, action];
    });
  }, []);

  const unregisterAction = useCallback((id: string) => {
    setActions((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const registerCenterAction = useCallback((action: HeaderAction) => {
    setCenterActions((prev) => {
      // Remove existing action with same id if it exists
      const filtered = prev.filter((a) => a.id !== action.id);
      return [...filtered, action];
    });
  }, []);

  const unregisterCenterAction = useCallback((id: string) => {
    setCenterActions((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const setLabel = useCallback((newLabel: HeaderLabel) => {
    setLabelState(newLabel);
  }, []);

  const clearLabel = useCallback(() => {
    setLabelState(undefined);
  }, []);

  const addHeaderClass = useCallback((className: string) => {
    setHeaderClasses((prev) => (prev.includes(className) ? prev : [...prev, className]));
  }, []);

  const removeHeaderClass = useCallback((className: string) => {
    setHeaderClasses((prev) => prev.filter((cls) => cls !== className));
  }, []);

  const clearHeaderClasses = useCallback(() => {
    setHeaderClasses([]);
  }, []);

  return (
    <HeaderActionsContext.Provider value={{ actions, centerActions, label, headerClasses, registerAction, unregisterAction, registerCenterAction, unregisterCenterAction, setLabel, clearLabel, addHeaderClass, removeHeaderClass, clearHeaderClasses }}>
      {children}
    </HeaderActionsContext.Provider>
  );
}

export function useHeaderActions() {
  const context = useContext(HeaderActionsContext);
  if (context === undefined) {
    throw new Error("useHeaderActions must be used within a HeaderActionsProvider");
  }
  return context;
}
